import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users, DollarSign, Building2 } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=1200";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [e, setE] = useState(null);
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get(`/events/${id}`).then((r) => setE(r.data)).catch(() => {}); }, [id]);

  const apply = async () => {
    setSubmitting(true);
    try {
      await api.post(`/events/${id}/apply`, { message: msg });
      toast.success("Application submitted! The organizer will review it.");
      setOpen(false);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSubmitting(false); }
  };

  if (!e) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-4 border-teal-200 border-t-[#0FB5A6] animate-spin" /></div>;

  const pin = L.divIcon({ className: "gr-pin", html: `<div class="gr-pin-inner gr-pin-live"><svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z"/></svg></div>`, iconSize: [40, 40], iconAnchor: [20, 20] });

  return (
    <div>
      <div className="relative h-[40vh] min-h-[300px]">
        <img src={e.image_url || FALLBACK} alt={e.title} className="w-full h-full object-cover" onError={(ev) => (ev.currentTarget.src = FALLBACK)} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-black/10" />
        <div className="absolute bottom-6 left-0 right-0 max-w-6xl mx-auto px-5">
          <span className="px-3 py-1 rounded-full bg-[#F2635F] text-slate-900 text-xs font-bold">{e.city}</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight mt-3">{e.title}</h1>
          <p className="text-white/80 mt-2">Hosted by {e.organizer_name}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-teal-100 p-6">
            <h2 className="font-display text-xl font-bold mb-2">About this event</h2>
            <p className="text-slate-600 leading-relaxed">{e.description}</p>
          </div>
          {e.lat != null && (
            <div className="bg-white rounded-2xl border border-teal-100 p-6">
              <h2 className="font-display text-xl font-bold mb-1">Location</h2>
              <p className="text-sm text-slate-500 mb-4 flex items-center gap-1"><MapPin className="w-4 h-4 text-[#0FB5A6]" /> {e.venue}, {e.address}</p>
              <div className="h-64 rounded-xl overflow-hidden">
                <MapContainer center={[e.lat, e.lng]} zoom={14} className="w-full h-full" scrollWheelZoom={false}>
                  <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
                  <Marker position={[e.lat, e.lng]} icon={pin} />
                </MapContainer>
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="bg-white rounded-2xl border border-teal-100 p-6 sticky top-20">
            <div className="space-y-3 text-sm text-slate-600">
              <Row icon={<CalendarDays className="w-4 h-4 text-[#0FB5A6]" />} label="Dates" value={`${e.start_date}${e.end_date ? ` – ${e.end_date}` : ""}`} />
              <Row icon={<Users className="w-4 h-4 text-[#0FB5A6]" />} label="Vendor spots" value={`${e.spots_filled}/${e.spots_total} filled`} />
              <Row icon={<DollarSign className="w-4 h-4 text-[#0FB5A6]" />} label="Vendor fee" value={`$${Number(e.vendor_fee).toFixed(0)}`} />
              <Row icon={<Building2 className="w-4 h-4 text-[#0FB5A6]" />} label="Venue" value={e.venue} />
            </div>

            {(!user || user.role === "owner") ? (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="w-full mt-6 rounded-full h-12 font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white active:scale-95 transition-transform"
                    data-testid="apply-event-btn"
                    onClick={(ev) => { if (!user) { ev.preventDefault(); toast.error("Log in as a truck owner to apply"); navigate("/login"); } }}
                  >
                    Apply as a vendor
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-display">Apply to {e.title}</DialogTitle></DialogHeader>
                  <p className="text-sm text-slate-500">Tell the organizer about your truck and why you're a great fit.</p>
                  <Textarea value={msg} onChange={(ev) => setMsg(ev.target.value)} placeholder="Hi! We're Titletown Tacos, we'd love to serve at your event…" rows={4} data-testid="apply-message" />
                  <Button onClick={apply} disabled={submitting} className="rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white" data-testid="submit-application-btn">
                    {submitting ? "Submitting…" : "Submit application"}
                  </Button>
                </DialogContent>
              </Dialog>
            ) : (
              <p className="text-sm text-slate-400 mt-6">Only food truck owners can apply to events.</p>
            )}
            {(!user || user.role === "owner") && (
              <p className="text-xs text-slate-400 mt-3 text-center">Event applications require a Pro plan or higher.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon, label, value }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-teal-50 last:border-0">
    <span className="flex items-center gap-2 text-slate-500">{icon} {label}</span>
    <span className="font-bold text-slate-800">{value}</span>
  </div>
);
